import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { AlertTriangle, ArrowRight, FileUp, MinusCircle, UploadCloud } from 'lucide-react';
import mcpServerService from '../../../services/mcpServerService';
import sharedStyles from '../../PlatformAdmin/PlatformAdmin.module.scss';
import styles from '../McpServerDetail.module.scss';
import InfoTip from '../../shared/InfoTip';
import { IMPORT_MODE_COPY, describeError } from '../mcpUtils';

const MODES = [
  { value: 'file', label: 'Upload' },
  { value: 'paste', label: 'Paste' },
  { value: 'url', label: 'URL' },
];

const SOURCE_TYPES = [
  { value: 'auto', label: 'Detect automatically (recommended)' },
  { value: 'openapi', label: 'OpenAPI 3.x' },
  { value: 'swagger', label: 'Swagger 2.0' },
  { value: 'postman', label: 'Postman collection v2.1' },
  { value: 'curl', label: 'cURL command(s)' },
];

/**
 * How a re-import treats the tools already on the server. Sync is the default
 * because it is the only one that keeps the customer's curation: renames,
 * on/off choices, order and hand-edited descriptions survive it.
 */
const IMPORT_MODES = ['sync', 'append', 'replace'].map((value) => ({
  value,
  ...IMPORT_MODE_COPY[value],
}));

const ACCEPT = '.json,.yaml,.yml,.txt,application/json,text/yaml,text/plain';
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 40; // ~2 minutes

/**
 * Import a spec (§3).
 *
 * `source_type` defaults to `auto` because customers mislabel formats
 * constantly — a Postman export saved as openapi.json is the classic — and the
 * parser sniffs, corrects, and records a warning we surface below.
 */
const ImportSpecTab = ({
  server,
  tools,
  onMutated,
  onGoToTools,
  notify,
  presetMode = null,
  onPresetApplied,
}) => {
  const [mode, setMode] = useState('file');
  const [sourceType, setSourceType] = useState('auto');
  const [content, setContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [url, setUrl] = useState('');
  const [importMode, setImportMode] = useState(presetMode || 'sync');
  // The mode the report on screen came from — the picker may have moved since.
  const [reportMode, setReportMode] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [importing, setImporting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [baseUrlDraft, setBaseUrlDraft] = useState('');
  const [savingBaseUrl, setSavingBaseUrl] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const hasExistingTools = tools.length > 0;

  useEffect(() => () => pollRef.current && clearTimeout(pollRef.current), []);

  // Arriving from a lint finding's "Re-import spec (sync)" link.
  useEffect(() => {
    if (!presetMode) return;
    setImportMode(presetMode);
    onPresetApplied?.();
  }, [presetMode, onPresetApplied]);

  const readFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      setContent(text);
      setFileName(file.name);
      setError(null);
    } catch (e) {
      setError("Couldn't read that file.");
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragOver(false);
    readFile(event.dataTransfer?.files?.[0]);
  };

  /**
   * The import can return before parsing finishes. Rather than declaring
   * failure, poll the tool list until it materialises.
   */
  const pollForTools = useCallback(
    (attempt = 0) => {
      if (attempt >= POLL_MAX_ATTEMPTS) {
        setPolling(false);
        notify('Import is still running. Refresh in a moment to see the tools.', 'info');
        return;
      }
      pollRef.current = setTimeout(async () => {
        try {
          const data = await mcpServerService.listTools(server.id);
          const list = Array.isArray(data) ? data : data?.tools || [];
          if (list.length > 0) {
            setPolling(false);
            await onMutated({ reloadServer: true });
            notify(`${list.length} tools imported.`, 'success');
            return;
          }
        } catch (e) {
          /* keep waiting — a transient failure shouldn't end the poll */
        }
        pollForTools(attempt + 1);
      }, POLL_INTERVAL_MS);
    },
    [server.id, onMutated, notify]
  );

  const handleImport = async () => {
    if (mode === 'url' && !url.trim()) {
      setError('Enter the URL of the spec.');
      return;
    }
    if (mode !== 'url' && !content.trim()) {
      setError(mode === 'file' ? 'Choose a spec file first.' : 'Paste the spec first.');
      return;
    }
    // Replacing throws away the customer's curation and any hand-edited
    // descriptions, so it gets a deliberate confirmation.
    if (importMode === 'replace' && hasExistingTools) {
      setConfirmOpen(true);
      return;
    }
    await runImport();
  };

  const runImport = async () => {
    setConfirmOpen(false);
    setImporting(true);
    setError(null);
    setReport(null);

    // With nothing imported yet every mode does the same thing, so leave it to
    // the API's default.
    const sentMode = hasExistingTools ? importMode : null;
    const body = { source_type: sourceType };
    if (sentMode) body.mode = sentMode;
    if (mode === 'url') body.url = url.trim();
    else body.content = content;

    try {
      const result = await mcpServerService.importSpec(server.id, body);
      const nextReport = result?.report || null;
      setReport(nextReport);
      setReportMode(sentMode);
      setBaseUrlDraft(nextReport?.base_url || server.upstream_base_url || '');

      const status = result?.status;
      if (status && status !== 'completed' && status !== 'failed') {
        setPolling(true);
        pollForTools();
      } else if (status === 'failed') {
        // A failed import normally arrives as a 400 with the reason (caught
        // below); this covers a job reported as failed in a 200.
        setError(
          result?.error ||
            nextReport?.error ||
            'The server could not parse that spec. Check the format and try again.'
        );
      } else {
        await onMutated({ reloadServer: true });
        if (sentMode === 'sync') {
          notify('Spec synced — see the summary below.', 'success');
        } else {
          const count = nextReport?.tool_count ?? 0;
          notify(`${count} ${count === 1 ? 'tool' : 'tools'} found.`, 'success');
        }
      }
    } catch (e) {
      setError(describeError(e, "Couldn't import that spec."));
    } finally {
      setImporting(false);
    }
  };

  const handleSaveBaseUrl = async () => {
    if (!/^https?:\/\//i.test(baseUrlDraft.trim())) {
      setError('The base URL must start with http:// or https://');
      return;
    }
    setSavingBaseUrl(true);
    try {
      await mcpServerService.updateServer(server.id, { upstream_base_url: baseUrlDraft.trim() });
      await onMutated({ reloadServer: true, reloadTools: false });
      notify('Upstream base URL saved.', 'success');
    } catch (e) {
      notify(describeError(e, "Couldn't save the base URL."), 'error');
    } finally {
      setSavingBaseUrl(false);
    }
  };

  const busy = importing || polling;
  const warnings = report?.warnings || [];
  const skipped = report?.skipped || [];
  const missingBaseUrl = !!report && !report.base_url && !server.upstream_base_url;

  return (
    <>
      {/* A server imported in an earlier session has no `report` in state — the
          API keeps no retrievable copy of it — so show what the server object
          does tell us, rather than an empty form that looks like nothing was
          ever imported. */}
      {!report && tools.length > 0 && (
        <div className={styles.section}>
          <div className={styles.reportHeadline}>
            <Typography className={styles.sectionTitle}>Already imported</Typography>
            <Button
              className={sharedStyles.outlineBtn}
              variant="outlined"
              endIcon={<ArrowRight size={16} />}
              onClick={onGoToTools}
            >
              Go to tools
            </Button>
          </div>
          <div className={styles.reportFacts}>
            <div className={styles.fact}>
              <span className={styles.factLabel}>Tools</span>
              <span className={styles.factValue}>{tools.length}</span>
            </div>
            {server.source_type && (
              <div className={styles.fact}>
                <span className={styles.factLabel}>Imported from</span>
                <span className={styles.factValue}>{server.source_type}</span>
              </div>
            )}
            {server.upstream_base_url && (
              <div className={styles.fact}>
                <span className={styles.factLabel}>Base URL</span>
                <span className={styles.factValue}>{server.upstream_base_url}</span>
              </div>
            )}
          </div>
          <span className={styles.sectionHint}>
            The warnings and skipped operations from that import aren’t kept, so they can’t be shown
            again. Importing below syncs, adds to or replaces these tools — you choose how.
          </span>
        </div>
      )}

      <div className={styles.section}>
        <Typography className={styles.sectionTitle}>
          {tools.length > 0 ? 'Re-import a spec' : 'Import a spec'}
        </Typography>
        <span className={styles.sectionHint}>
          OpenAPI 3.0/3.1, Swagger 2.0, Postman collection v2.1, or one or more cURL commands.
        </span>

        <div className={styles.modeToggle}>
          {MODES.map((m) => (
            <button
              key={m.value}
              type="button"
              className={`${styles.modeBtn} ${mode === m.value ? styles.modeBtnActive : ''}`}
              onClick={() => setMode(m.value)}
            >
              {m.label}
            </button>
          ))}
        </div>

        {mode === 'file' && (
          <>
            <div
              className={`${styles.dropZone} ${dragOver ? styles.dropZoneActive : ''}`}
              onDrop={handleDrop}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOver(false);
              }}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
            >
              <UploadCloud size={32} color="var(--text-secondary)" />
              <span className={styles.dropTitle}>
                {fileName || 'Drop your spec here, or click to choose'}
              </span>
              <span className={styles.dropHint}>
                {fileName
                  ? `${content.length.toLocaleString()} characters loaded`
                  : '.json, .yaml, .yml or .txt'}
              </span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT}
              style={{ display: 'none' }}
              onChange={(e) => {
                readFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </>
        )}

        {mode === 'paste' && (
          <textarea
            className={styles.codeArea}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={'{\n  "openapi": "3.1.0",\n  ...\n}'}
            spellCheck={false}
          />
        )}

        {mode === 'url' && (
          <TextField
            label="Spec URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            fullWidth
            size="small"
            placeholder="https://api.acme.com/openapi.json"
          />
        )}

        <div className={styles.row}>
          <FormControl size="small" className={styles.grow}>
            <InputLabel>Format</InputLabel>
            <Select label="Format" value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
              {SOURCE_TYPES.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {hasExistingTools && (
          <Box>
            <Typography
              className={styles.sectionTitle}
              sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}
            >
              What happens to the {tools.length} current tools
              <InfoTip title={IMPORT_MODE_COPY.sectionTip} />
            </Typography>
            <RadioGroup value={importMode} onChange={(e) => setImportMode(e.target.value)}>
              {IMPORT_MODES.map((m) => (
                <FormControlLabel
                  key={m.value}
                  value={m.value}
                  control={<Radio size="small" />}
                  sx={{ alignItems: 'flex-start', mt: 0.75 }}
                  label={
                    <Box sx={{ pt: '7px' }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.75 }}
                      >
                        {m.label}
                        <InfoTip title={m.tooltip} size={14} />
                      </Typography>
                      <span className={styles.sectionHint}>{m.hint}</span>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </Box>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Button
            className={sharedStyles.primaryBtn}
            variant="contained"
            onClick={handleImport}
            disabled={busy}
            startIcon={busy ? <CircularProgress size={15} color="inherit" /> : <FileUp size={16} />}
          >
            {importing ? 'Importing…' : polling ? 'Still parsing…' : 'Import'}
          </Button>
          {polling && (
            <span className={sharedStyles.muted}>
              The server is still working through the spec — this page will update itself.
            </span>
          )}
        </Box>
      </div>

      {/* ── The report. Warnings and skipped operations stay expanded: they are
            the only answer to "why is this tool missing / why does this
            parameter have a weird name", and the customer never sees the
            original spec again. ── */}
      {report && (
        <div className={styles.section}>
          <div className={styles.reportHeadline}>
            <Typography className={styles.sectionTitle}>
              {reportMode === 'sync'
                ? 'Spec synced'
                : `${report.tool_count ?? 0} ${report.tool_count === 1 ? 'tool' : 'tools'} found`}
              {report.source_type ? ` from ${report.source_type}` : ''}
            </Typography>
            <Button
              className={sharedStyles.outlineBtn}
              variant="outlined"
              endIcon={<ArrowRight size={16} />}
              onClick={onGoToTools}
            >
              Choose which to expose
            </Button>
          </div>

          <div className={styles.reportFacts}>
            {report.base_url && (
              <div className={styles.fact}>
                <span className={styles.factLabel}>Base URL</span>
                <span className={styles.factValue}>{report.base_url}</span>
              </div>
            )}
            {report.suggested_auth_type && (
              <div className={styles.fact}>
                <span className={styles.factLabel}>Suggested auth</span>
                <span className={styles.factValue}>{report.suggested_auth_type}</span>
              </div>
            )}
            {report.source_type && (
              <div className={styles.fact}>
                <span className={styles.factLabel}>Detected format</span>
                <span className={styles.factValue}>{report.source_type}</span>
              </div>
            )}
          </div>

          {missingBaseUrl && (
            <>
              <Alert severity="warning">
                This spec had no <code>servers[]</code> entry, so we don’t know where to send calls.
                Enter the upstream base URL before publishing.
              </Alert>
              <div className={styles.row}>
                <TextField
                  label="Upstream base URL"
                  value={baseUrlDraft}
                  onChange={(e) => setBaseUrlDraft(e.target.value)}
                  size="small"
                  className={styles.grow}
                  placeholder="https://api.acme.com/v1"
                />
                <Button
                  className={sharedStyles.outlineBtn}
                  variant="outlined"
                  onClick={handleSaveBaseUrl}
                  disabled={savingBaseUrl}
                >
                  {savingBaseUrl ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </>
          )}

          {warnings.length > 0 && (
            <div className={`${styles.reportGroup} ${styles.reportGroupWarn}`}>
              <span className={styles.reportGroupTitle}>
                <AlertTriangle size={14} color="#d97706" />
                {warnings.length} {warnings.length === 1 ? 'warning' : 'warnings'}
              </span>
              <ul className={styles.reportList}>
                {warnings.map((w, i) => (
                  <li key={i}>{typeof w === 'string' ? w : w?.message || JSON.stringify(w)}</li>
                ))}
              </ul>
            </div>
          )}

          {skipped.length > 0 && (
            <div className={styles.reportGroup}>
              <span className={styles.reportGroupTitle}>
                <MinusCircle size={14} color="var(--text-secondary)" />
                {skipped.length} skipped — this is why the count may be lower than you expected
              </span>
              <ul className={styles.reportList}>
                {skipped.map((s, i) => (
                  <li key={i}>
                    <span className={styles.reportOp}>
                      {typeof s === 'string' ? s : s?.operation || 'operation'}
                    </span>
                    {typeof s === 'object' && s?.reason ? ` — ${s.reason}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Replace the existing tools?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            This discards the {tools.length} tools already on this server — including which ones you
            enabled and any descriptions you edited by hand — and rebuilds them from the new spec.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button className={sharedStyles.primaryBtn} variant="contained" onClick={runImport}>
            Replace and import
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ImportSpecTab;
